import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowsAltH } from '@react-icons/all-files/fa/FaArrowsAltH';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import ProfaseDataGrid from '../../commons/Datagrid';
import { DeleteButton, EditButton, RenderActions } from '../../commons/DataGridActions';
import MoneyFormatter from '../../commons/componentes/MoneyFormatter';
import WindowHeader from '../../commons/WindowHeader';
import PageAlert, { usePageAlert } from '../../commons/PageAlert';
import Confirm, { useConfirm } from '../../commons/Confirm';
import ServicioCrearModal from './ServicioCrearModal';
import ServicioEditarModal from './ServicioEditarModal';
import { useAppDispatch, useAppSelector } from '../../store';
import { getEntities as getProveedores } from '../proveedor.reducer';
import {
  deleteEntity as deleteCategoriaServicio,
  getEntities as getCategoriaServicios,
  getEntitiesEspeciales,
} from '../categoria-servicio.reducer';

const HEADER_ESPECIALES_BG = '#9a3412';

const serviciosFilters = [
  { label: 'Descripción', name: 'descripcion' },
  { label: 'Alias de Facturación', name: 'alias' },
];

function servicioRowLabel(row: any) {
  const label = row.descripcion ?? row.alias ?? row.aliasFacturacion ?? row.aliasDeFacturacion;
  return label != null && String(label).trim() !== '' ? String(label) : `ID ${row.id}`;
}

function buildServicioGridCols(
  onEdit: (id: number) => void,
  onDelete: (row: any) => void,
  editTitle: string,
  deleteTitle: string,
) {
  return [
    { field: 'descripcion', headerName: 'Descripción', flex: 2.5 },
    {
      field: 'alias',
      headerName: 'Alias de Facturación',
      flex: 1.5,
      renderCell: (p: any) =>
        p.row.aliasFacturacion ?? p.row.aliasDeFacturacion ?? p.row.alias ?? '—',
    },
    {
      field: 'precio',
      headerName: 'Precio',
      flex: 1,
      renderCell: (p: any) => <MoneyFormatter value={p.row.precio} />,
    },
    {
      field: '__actions',
      headerName: '',
      flex: 1,
      renderCell: (p: any) => (
        <RenderActions>
          <EditButton onClick={() => onEdit(Number(p.row.id))} title={editTitle} />
          <DeleteButton onClick={() => onDelete(p.row)} title={deleteTitle} />
        </RenderActions>
      ),
    },
  ];
}

type ServiciosFilaExpandida = 'ninguno' | 'servicios' | 'especiales';

function ExpandFilaButton({
  expandida,
  onToggle,
  expandirLabel,
  contraerLabel,
}: {
  expandida: boolean;
  onToggle: () => void;
  expandirLabel: string;
  contraerLabel: string;
}) {
  return (
    <button
      type="button"
      className="btn btn-sm btn-light d-inline-flex align-items-center justify-content-center"
      style={{ width: 36, height: 32, padding: 0 }}
      title={expandida ? contraerLabel : expandirLabel}
      aria-label={expandida ? 'Contraer panel' : 'Expandir panel a ancho completo'}
      onClick={onToggle}
    >
      <FaArrowsAltH />
    </button>
  );
}

function ProveedorServiciosTable() {
  const { id: idParam } = useParams<'id'>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entities = useAppSelector(state => state.proveedor.entities);
  const loading = useAppSelector(state => state.proveedor.loading);
  const totalItems = useAppSelector(state => state.proveedor.totalItems);

  const serviciosEntities = useAppSelector(state => state.categoriaServicio.entities);
  const serviciosLoading = useAppSelector(state => state.categoriaServicio.loading);
  const serviciosTotalItems = useAppSelector(state => state.categoriaServicio.totalItems);

  const especialesEntities = useAppSelector(state => state.categoriaServicio.entitiesEspeciales);
  const especialesLoading = useAppSelector(state => state.categoriaServicio.loadingEspeciales);
  const especialesTotalItems = useAppSelector(state => state.categoriaServicio.totalItemsEspeciales);
  const categoriaServicioUpdating = useAppSelector(state => state.categoriaServicio.updating);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  const [rows, setRows] = useState<any[]>([]);
  const [serviciosRows, setServiciosRows] = useState<any[]>([]);
  const [especialesRows, setEspecialesRows] = useState<any[]>([]);
  const [serviciosFilaExpandida, setServiciosFilaExpandida] = useState<ServiciosFilaExpandida>('ninguno');
  const [servicioEditId, setServicioEditId] = useState<number | null>(null);
  const [servicioEditEspecial, setServicioEditEspecial] = useState(false);
  const [servicioCrearTipo, setServicioCrearTipo] = useState<'normal' | 'especial' | null>(null);
  const [serviciosGridsRefreshKey, setServiciosGridsRefreshKey] = useState(0);

  const selectedId = useMemo(() => {
    if (idParam == null || idParam === '') return null;
    const n = Number(idParam);
    return Number.isFinite(n) ? n : null;
  }, [idParam]);

  const selectedCompania = useMemo(
    () => entities?.find((e: any) => Number(e.id) === selectedId),
    [entities, selectedId],
  );

  useEffect(() => {
    setServiciosFilaExpandida('ninguno');
    setServicioCrearTipo(null);
  }, [selectedId]);

  useEffect(() => {
    setRows(entities ?? []);
  }, [entities]);

  useEffect(() => {
    setServiciosRows(serviciosEntities ?? []);
  }, [serviciosEntities]);

  useEffect(() => {
    setEspecialesRows(especialesEntities ?? []);
  }, [especialesEntities]);

  const toggleServiciosFilaExpandida = () => {
    setServiciosFilaExpandida(v => (v === 'servicios' ? 'ninguno' : 'servicios'));
  };

  const toggleEspecialesFilaExpandida = () => {
    setServiciosFilaExpandida(v => (v === 'especiales' ? 'ninguno' : 'especiales'));
  };

  const seleccionarCompania = (row: { id: number }) => {
    navigate(`/companias/${row.id}`);
  };

  const deseleccionarCompania = () => {
    navigate('/companias');
  };

  const renderButtonsCell = (params: any) => {
    const rowId = Number(params.row.id);
    if (selectedId != null && rowId === selectedId) {
      return (
        <button type="button" className="btn btn-primary btn-sm" onClick={deseleccionarCompania}>
          Deseleccionar
        </button>
      );
    }
    return (
      <button type="button" className="btn btn-primary btn-sm" onClick={() => seleccionarCompania(params.row)}>
        Seleccionar
      </button>
    );
  };

  const companiaCols = [
    { field: 'nombre', headerName: 'Nombre', flex: 2 },
    { field: '__actions', headerName: '', flex: 1, renderCell: renderButtonsCell },
  ];

  const companiaFilters = [{ label: 'Nombre', name: 'nombre' }];

  const fetchCompaniasData = (params: any) => {
    dispatch(getProveedores(params));
  };

  const fetchServiciosData = (params: any) => {
    dispatch(
      getCategoriaServicios({
        ...params,
        proveedorId: selectedId ?? undefined,
        filters: [
          ...(params.filters ?? []),
          { filterName: 'especial', filterValue: 'false' },
        ],
      }),
    );
  };

  useEffect(() => {
    setConfirmLoading(categoriaServicioUpdating);
  }, [categoriaServicioUpdating, setConfirmLoading]);

  const openServicioEdit = useCallback((id: number, especial: boolean) => {
    setServicioEditId(id);
    setServicioEditEspecial(especial);
  }, []);

  const refreshServiciosGrids = useCallback(() => {
    setServiciosGridsRefreshKey(k => k + 1);
  }, []);

  const confirmDeleteServicio = useCallback(
    (row: any, especial: boolean) => {
      const label = servicioRowLabel(row);
      ask(
        <>
          ¿Confirmas que deseas eliminar el servicio <strong>{label}</strong>? Esta acción no se puede
          deshacer.
        </>,
        () => {
          void (async () => {
            try {
              await dispatch(deleteCategoriaServicio(row.id)).unwrap();
              dismiss();
              showToast(
                'success',
                especial
                  ? 'Servicio especial eliminado correctamente.'
                  : 'Servicio eliminado correctamente.',
              );
              refreshServiciosGrids();
            } catch {
              dismiss();
              showToast(
                'error',
                especial
                  ? 'No se pudo eliminar el servicio especial.'
                  : 'No se pudo eliminar el servicio.',
              );
            }
          })();
        },
        {
          title: especial ? 'Eliminar servicio especial' : 'Eliminar servicio',
          confirmLabel: 'Eliminar',
          variant: 'danger',
        },
      );
    },
    [ask, dismiss, dispatch, showToast, refreshServiciosGrids],
  );

  const serviciosCols = useMemo(
    () =>
      buildServicioGridCols(
        id => openServicioEdit(id, false),
        row => confirmDeleteServicio(row, false),
        'Editar servicio',
        'Eliminar servicio',
      ),
    [openServicioEdit, confirmDeleteServicio],
  );

  const especialesCols = useMemo(
    () =>
      buildServicioGridCols(
        id => openServicioEdit(id, true),
        row => confirmDeleteServicio(row, true),
        'Editar servicio especial',
        'Eliminar servicio especial',
      ),
    [openServicioEdit, confirmDeleteServicio],
  );

  const fetchEspecialesData = (params: any) => {
    dispatch(
      getEntitiesEspeciales({
        ...params,
        proveedorId: selectedId ?? undefined,
      }),
    );
  };

  const companiaRowCount = Number.isFinite(totalItems) && totalItems > 0 ? totalItems : rows.length;
  const serviciosRowCount =
    Number.isFinite(serviciosTotalItems) && serviciosTotalItems > 0 ? serviciosTotalItems : serviciosRows.length;
  const especialesRowCount =
    Number.isFinite(especialesTotalItems) && especialesTotalItems > 0
      ? especialesTotalItems
      : especialesRows.length;

  const getRowClassName = (params: any) =>
    selectedId != null && Number(params.id) === selectedId ? 'selected' : '';

  const headerDeseleccionar =
    selectedId != null ? (
      <button type="button" className="btn btn-sm btn-light" onClick={deseleccionarCompania}>
        Deseleccionar
      </button>
    ) : undefined;

  const companiaSuffix =
    selectedId != null && selectedCompania?.nombre ? ` — ${selectedCompania.nombre}` : '';

  const serviciosTitle = `Servicios${companiaSuffix}`;
  const especialesTitle = `Servicios Especiales${companiaSuffix}`;

  const serviciosExpandido = serviciosFilaExpandida === 'servicios';
  const especialesExpandido = serviciosFilaExpandida === 'especiales';

  const handleCloseServicioEdit = () => {
    setServicioEditId(null);
    setServicioEditEspecial(false);
    refreshServiciosGrids();
  };

  const handleServicioSaved = useCallback(() => {
    showToast(
      'success',
      servicioEditEspecial
        ? 'Servicio especial modificado con éxito.'
        : 'Servicio modificado con éxito.',
      { actionLabel: 'Aceptar' },
    );
  }, [servicioEditEspecial, showToast]);

  const handleCloseServicioCrear = () => {
    setServicioCrearTipo(null);
    refreshServiciosGrids();
  };

  const serviciosHeaderExtraRight = (
    <div className="d-flex align-items-center gap-1 flex-shrink-0">
      <ExpandFilaButton
        expandida={serviciosExpandido}
        onToggle={toggleServiciosFilaExpandida}
        expandirLabel="Expandir Servicios al 100 % del ancho (ocultar Servicios Especiales)"
        contraerLabel="Restaurar ancho: mostrar de nuevo Servicios Especiales junto a Servicios"
      />
      {selectedId != null ? (
        <button
          type="button"
          className="btn btn-sm btn-light d-inline-flex align-items-center justify-content-center"
          style={{ width: 36, height: 32, padding: 0 }}
          title="Crear servicio"
          aria-label="Crear servicio"
          onClick={() => setServicioCrearTipo('normal')}
        >
          <FaPlus />
        </button>
      ) : null}
    </div>
  );

  const especialesHeaderExtraRight = (
    <div className="d-flex align-items-center gap-1 flex-shrink-0">
      <ExpandFilaButton
        expandida={especialesExpandido}
        onToggle={toggleEspecialesFilaExpandida}
        expandirLabel="Expandir Servicios Especiales al 100 % del ancho (ocultar Servicios)"
        contraerLabel="Restaurar ancho: mostrar de nuevo Servicios junto a Servicios Especiales"
      />
      {selectedId != null ? (
        <button
          type="button"
          className="btn btn-sm btn-light d-inline-flex align-items-center justify-content-center"
          style={{ width: 36, height: 32, padding: 0 }}
          title="Crear servicio especial"
          aria-label="Crear servicio especial"
          onClick={() => setServicioCrearTipo('especial')}
        >
          <FaPlus />
        </button>
      ) : null}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%' }}>
      <div className="p-3 border" style={{ width: '100%' }}>
        <WindowHeader title="Compañías" headerExtraRight={headerDeseleccionar} />
        <div className="pt-3">
          <ProfaseDataGrid
            cols={companiaCols}
            title=""
            filters={companiaFilters}
            fetch={fetchCompaniasData}
            rowCount={companiaRowCount}
            loading={loading}
            rows={rows}
            defaultSortField="nombre"
            getRowClassName={getRowClassName}
          />
        </div>
      </div>

      <Confirm config={confirmConfig} />
      {alert ? <PageAlert alert={alert} /> : null}

      <div className="row g-0" style={{ width: '100%' }}>
        {serviciosFilaExpandida !== 'especiales' && (
          <div
            className={serviciosExpandido ? 'col-12 border p-3' : 'col-12 col-lg-6 border p-3'}
            style={{ minWidth: 0, overflow: 'hidden' }}
          >
            <WindowHeader title={serviciosTitle} headerExtraRight={serviciosHeaderExtraRight} />
            <div className="pt-3">
              <ProfaseDataGrid
                key={`servicios-${selectedId ?? 'sin-proveedor'}-${serviciosGridsRefreshKey}`}
                cols={serviciosCols}
                title=""
                filters={serviciosFilters}
                fetch={fetchServiciosData}
                rowCount={serviciosRowCount}
                loading={serviciosLoading}
                rows={serviciosRows}
                defaultSortField="descripcion"
              />
            </div>
          </div>
        )}

        {serviciosFilaExpandida !== 'servicios' && (
          <div
            className={especialesExpandido ? 'col-12 border p-3' : 'col-12 col-lg-6 border p-3'}
            style={{ minWidth: 0, overflow: 'hidden' }}
          >
            <WindowHeader
              title={especialesTitle}
              headerBackground={HEADER_ESPECIALES_BG}
              headerExtraRight={especialesHeaderExtraRight}
            />
            <div className="pt-3">
              <ProfaseDataGrid
                key={`especiales-${selectedId ?? 'sin-proveedor'}-${serviciosGridsRefreshKey}`}
                cols={especialesCols}
                title=""
                filters={serviciosFilters}
                fetch={fetchEspecialesData}
                rowCount={especialesRowCount}
                loading={especialesLoading}
                rows={especialesRows}
                defaultSortField="descripcion"
              />
            </div>
          </div>
        )}
      </div>

      <ServicioCrearModal
        open={servicioCrearTipo != null}
        proveedorId={selectedId}
        companiaNombre={selectedCompania?.nombre}
        especial={servicioCrearTipo === 'especial'}
        onClose={handleCloseServicioCrear}
      />

      <ServicioEditarModal
        open={servicioEditId != null}
        servicioId={servicioEditId}
        especial={servicioEditEspecial}
        onClose={handleCloseServicioEdit}
        onSaved={handleServicioSaved}
      />
    </div>
  );
}

export default ProveedorServiciosTable;
